import { apiService } from './apiService';
import { ChatMessage } from '../models/clinicAgent';
import { API_BASE_URL } from '../config/env';
import { ChatHistoryRow, chatRowToMessage } from './chatRows';

// A page of transcript plus whether older pages remain (DRF `next` cursor).
export interface ChatHistoryPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

// The pre-conversations history endpoint. Only the single-thread fallback uses
// it, while GET /api/clinic/agent/conversations/ is not deployed (see
// ClinicAgentRoot). Remove it together with that fallback.
export const clinicChatHistoryService = {
  // Load one page of the clinician's own chat transcript. GET only — never POST
  // /api/chat/ from the clinic client (that runs the patient coach persona).
  // Rows arrive newest-first; we return them chronological for rendering, so
  // page 1 is the most recent slice and higher pages are progressively older.
  getHistory: async (page: number = 1): Promise<ChatHistoryPage> => {
    const data = await apiService.get(`${API_BASE_URL}/chat/?page=${page}`);
    const rows: ChatHistoryRow[] = data?.results ?? data ?? [];
    // This API paginates with { actualPage, totalPages, perpage } (not DRF's
    // next/previous). Prefer page counters; fall back to a full-page heuristic
    // or a DRF `next` cursor so this keeps working if the backend shape changes.
    const totalPages = Number(data?.totalPages);
    const actualPage = Number(data?.actualPage ?? page);
    const perpage = Number(data?.perpage);
    const hasMore = Number.isFinite(totalPages) && totalPages > 0
      ? actualPage < totalPages
      : data?.next != null
        ? Boolean(data.next)
        : Number.isFinite(perpage) && perpage > 0
          ? rows.length >= perpage
          : false;
    // API is newest-first; reverse to chronological.
    return { messages: rows.map(chatRowToMessage).reverse(), hasMore };
  },
};
